import React, { SyntheticEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import PrimaryButton from 'commons/src/components/buttons/PrimaryButton';
import CheckBox from 'commons/src/components/input/Checkbox';
import Input from 'commons/src/components/input/Input';
import InputInfo from 'commons/src/components/input/InputInfo';
import RadioButtons from 'commons/src/components/input/Radio';
import MaterialIcon from 'commons/src/components/MaterialIcon';
import { sensorUnits as sensorUnitLabels } from 'commons/src/constants';
import { LocationType, Units } from 'commons/src/models/commonTypeScript';
import CollapsibleSection from '../../../components/CollapsibleSection/CollapsibleSection';
import { SelectionModal, SelectionModalItem } from '../../../components/SelectionModal';
import { paths } from '../../../constants';
import { Store } from '../../../reducers';
import FileUploadField from './FileUploadField';
import styles from './mqttPublisher.module.scss';
import {
    DEFAULT_MQTT_PORT,
    DEFAULT_MQTTS_PORT,
    DEFAULT_TOPIC_TEMPLATE,
    MQTT_EVENT_TYPES,
    MQTT_PROTOCOL,
    MQTTS_PROTOCOL,
    MqttPublisherAuthType,
    MqttPublisherConfig,
    MqttPublisherFormValues,
    MqttPublisherProtocol,
    TOPIC_TEMPLATE_VARIABLES,
} from './mqttPublisherTypes';

type Props = {
    onSubmit: (values: MqttPublisherFormValues) => Promise<{ clientId: string }>;
    submitLoading: boolean;
    initialConfig?: MqttPublisherConfig;
};

const MqttPublisherCreateForm = ({ onSubmit, submitLoading, initialConfig }: Props): React.ReactElement => {
    const { t: txt } = useTranslation();
    const navigate = useNavigate();
    const userUnits = useSelector((state: Store) => state.userSettings.units);
    const locations: LocationType[] = useSelector((state: Store) => state.locations.locations);

    const isEditMode = !!initialConfig;

    const [name, setName] = useState(initialConfig?.name || '');
    const [protocol, setProtocol] = useState<MqttPublisherProtocol>(initialConfig?.protocol || MQTT_PROTOCOL);
    const [host, setHost] = useState(initialConfig?.host || '');
    const [port, setPort] = useState(initialConfig?.port || DEFAULT_MQTT_PORT);
    const [username, setUsername] = useState(initialConfig?.username || '');
    const [password, setPassword] = useState('');
    const [clientCert, setClientCert] = useState<File | undefined>();
    const [clientKey, setClientKey] = useState<File | undefined>();
    const [caCert, setCaCert] = useState<File | undefined>();
    const [eventTypes, setEventTypes] = useState<string[]>(
        initialConfig?.eventTypes || MQTT_EVENT_TYPES.map(e => e.value)
    );
    const [displayValidation, setDisplayValidation] = useState(false);
    const [topicTemplate, setTopicTemplate] = useState(initialConfig?.topicTemplate || '');
    const [showTopicHelp, setShowTopicHelp] = useState(false);
    const [selectedLocations, setSelectedLocations] = useState<string[]>(initialConfig?.locations || []);
    const [showBuildingsModal, setShowBuildingsModal] = useState(false);

    const [sensorUnits, setSensorUnits] = useState<Units>(
        initialConfig?.sensorUnits || {
            radonUnit: userUnits.radonUnit || 'bq',
            tempUnit: userUnits.tempUnit || 'c',
            pressureUnit: userUnits.pressureUnit || 'hpa',
            vocUnit: userUnits.vocUnit || 'ppb',
        }
    );

    useEffect(() => {
        setPort(protocol === MQTTS_PROTOCOL ? DEFAULT_MQTTS_PORT : DEFAULT_MQTT_PORT);
        if (protocol === MQTT_PROTOCOL) {
            setClientCert(undefined);
            setClientKey(undefined);
            setCaCert(undefined);
        }
    }, [protocol]);

    const hasCertsUploaded = isEditMode && initialConfig.authType === 'CERTIFICATE_BASED';

    const buildingItems: SelectionModalItem[] = locations.map(loc => ({ id: loc.id, label: loc.name }));
    const allLocationIds = locations.map(loc => loc.id);
    const isSubscribedToAll = selectedLocations.length === 0;
    const effectiveSelectedIds = isSubscribedToAll ? allLocationIds : selectedLocations;

    const handleBuildingsSave = (ids: string[]): void => {
        setSelectedLocations(ids.length === locations.length ? [] : ids);
    };

    const selectedLocationNames = locations.filter(loc => selectedLocations.includes(loc.id)).map(loc => loc.name);
    const buildingsText = isSubscribedToAll
        ? txt('MqttPublisher.AllBuildingsSelected')
        : selectedLocationNames.join(', ');

    const hasCreds = !!(username && password);
    const hasCerts = !!(clientCert && clientKey);
    const mqttsAuthValid = protocol !== MQTTS_PROTOCOL || hasCreds || hasCerts || isEditMode;

    const isValid =
        name.length > 0 &&
        host.length > 0 &&
        !/\s/.test(host) &&
        port >= 1 &&
        port <= 65535 &&
        eventTypes.length > 0 &&
        mqttsAuthValid;

    const handleSubmit = async (): Promise<void> => {
        if (!isValid) {
            setDisplayValidation(true);
            return;
        }
        try {
            const authType: MqttPublisherAuthType =
                protocol === MQTTS_PROTOCOL && hasCerts ? 'CERTIFICATE_BASED' : 'BASIC_AUTHENTICATION';
            const result = await onSubmit({
                name,
                protocol,
                host,
                port,
                authType,
                username: username || undefined,
                password: password || undefined,
                clientCertificate: clientCert,
                clientKey,
                caCertificate: caCert,
                active: isEditMode ? initialConfig.active : false,
                subscribedToAll: isSubscribedToAll,
                eventTypes,
                locations: selectedLocations,
                sensorUnits,
                topicTemplate: topicTemplate || undefined,
            });
            navigate(`/${paths.mqttPublisher}/${result.clientId}`);
        } catch {
            // error is handled by the hook
        }
    };

    return (
        <form>
            <div className="page-wrapper__medium">
                <CollapsibleSection title={txt('MqttPublisher.General')} testAttr="section-general">
                    <Input
                        id="publisherName"
                        label="MqttPublisher.Name"
                        type="text"
                        validate={displayValidation}
                        isValid={name.length > 0}
                        hint="MqttPublisher.NameRequired"
                        markedMandatory
                        currentValue={name}
                        onChange={(e: SyntheticEvent<HTMLInputElement>): void => setName(e.currentTarget.value)}
                    />
                    <div className={styles.buildingsSection}>
                        <div className={styles.buildingsSection__label}>{txt('MqttPublisher.Buildings')}</div>
                        <div className={styles.buildingsSection__row}>
                            <span>{buildingsText}</span>
                            <button
                                type="button"
                                className={styles.buildingsSection__editLink}
                                onClick={(): void => setShowBuildingsModal(true)}
                            >
                                <MaterialIcon name="edit" />
                                <span>{txt('MqttPublisher.ViewEditBuildings')}</span>
                            </button>
                        </div>
                    </div>
                </CollapsibleSection>
            </div>

            <div className="page-wrapper__medium">
                <CollapsibleSection title={txt('MqttPublisher.Connection')} testAttr="section-connection">
                    <div className={styles.hostPortRow}>
                        <Input
                            id="publisherHost"
                            label="MqttPublisher.HostIp"
                            type="text"
                            validate={displayValidation}
                            isValid={host.length > 0 && !/\s/.test(host)}
                            hint={/\s/.test(host) ? 'MqttPublisher.HostNoSpaces' : 'MqttPublisher.HostRequired'}
                            markedMandatory
                            currentValue={host}
                            onChange={(e: SyntheticEvent<HTMLInputElement>): void => setHost(e.currentTarget.value)}
                        />
                        <Input
                            id="publisherPort"
                            label="MqttPublisher.Port"
                            type="number"
                            validate={displayValidation}
                            isValid={port >= 1 && port <= 65535}
                            hint="MqttPublisher.PortRequired"
                            markedMandatory
                            currentValue={String(port)}
                            onChange={(e: SyntheticEvent<HTMLInputElement>): void =>
                                setPort(Number(e.currentTarget.value))
                            }
                        />
                    </div>
                    <RadioButtons
                        header="MqttPublisher.Protocol"
                        buttons={[
                            { value: MQTT_PROTOCOL, label: 'MQTT' },
                            { value: MQTTS_PROTOCOL, label: 'MQTTs' },
                        ]}
                        selectorName="protocol"
                        row
                        onChange={(e: SyntheticEvent<HTMLInputElement>): void =>
                            setProtocol(e.currentTarget.value as MqttPublisherProtocol)
                        }
                        value={protocol}
                        labelId="protocol"
                    />
                </CollapsibleSection>
            </div>

            <div className="page-wrapper__medium">
                <CollapsibleSection title={txt('MqttPublisher.Authentication')} testAttr="section-authentication">
                    <p className={styles.authDescription}>
                        {protocol === MQTTS_PROTOCOL
                            ? txt('MqttPublisher.AuthDescriptionMqtts')
                            : txt('MqttPublisher.AuthDescription')}
                    </p>
                    {displayValidation && protocol === MQTTS_PROTOCOL && !mqttsAuthValid && (
                        <p className={styles.authValidationHint}>{txt('MqttPublisher.MqttsAuthRequired')}</p>
                    )}
                    <div className={styles.hostPortRow}>
                        <Input
                            id="publisherUsername"
                            label="MqttPublisher.Username"
                            type="text"
                            validate={false}
                            currentValue={username}
                            onChange={(e: SyntheticEvent<HTMLInputElement>): void => setUsername(e.currentTarget.value)}
                        />
                        <Input
                            id="publisherPassword"
                            label="MqttPublisher.Password"
                            type="password"
                            validate={false}
                            currentValue={password}
                            placeholder={isEditMode ? txt('MqttPublisher.PasswordKeepCurrent') : undefined}
                            onChange={(e: SyntheticEvent<HTMLInputElement>): void => setPassword(e.currentTarget.value)}
                        />
                    </div>
                    {protocol === MQTTS_PROTOCOL && (
                        <>
                            <FileUploadField
                                label="MqttPublisher.ClientCertificate"
                                file={clientCert}
                                onChange={setClientCert}
                                accept=".crt,.pem,.key"
                                showValidation={false}
                                existingFileName={
                                    hasCertsUploaded ? txt('MqttPublisher.CertificateUploaded') : undefined
                                }
                            />
                            <FileUploadField
                                label="MqttPublisher.ClientKey"
                                file={clientKey}
                                onChange={setClientKey}
                                accept=".crt,.pem,.key"
                                showValidation={false}
                                existingFileName={
                                    hasCertsUploaded ? txt('MqttPublisher.CertificateUploaded') : undefined
                                }
                            />
                            <FileUploadField
                                label="MqttPublisher.CaCertificate"
                                file={caCert}
                                onChange={setCaCert}
                                accept=".crt,.pem,.key"
                                showValidation={false}
                                existingFileName={
                                    hasCertsUploaded ? txt('MqttPublisher.CertificateUploaded') : undefined
                                }
                            />
                        </>
                    )}
                </CollapsibleSection>
            </div>

            <div className="page-wrapper__medium">
                <CollapsibleSection title={txt('MqttPublisher.DataConfigurations')} testAttr="section-data-config">
                    <Input
                        id="topicTemplate"
                        label="MqttPublisher.TopicFormat"
                        type="text"
                        validate={false}
                        currentValue={topicTemplate}
                        placeholder={DEFAULT_TOPIC_TEMPLATE}
                        onChange={(e: SyntheticEvent<HTMLInputElement>): void =>
                            setTopicTemplate(e.currentTarget.value)
                        }
                    />
                    <button
                        type="button"
                        className={styles.topicHelpToggle}
                        onClick={(): void => setShowTopicHelp(prev => !prev)}
                    >
                        <MaterialIcon name={showTopicHelp ? 'expand_less' : 'expand_more'} />
                        <span>{txt('MqttPublisher.TopicHelpToggle')}</span>
                    </button>
                    {showTopicHelp && (
                        <div className={styles.topicHelpPanel}>
                            <p className={styles.topicHelpPanel__description}>
                                {txt('MqttPublisher.TopicHelpDescription')}
                            </p>
                            <div className={styles.topicHelpPanel__variables}>
                                <span className={styles.topicHelpPanel__variablesLabel}>
                                    {txt('MqttPublisher.TopicHelpVariables')}
                                </span>
                                <div className={styles.topicHelpPanel__chips}>
                                    {TOPIC_TEMPLATE_VARIABLES.map(variable => (
                                        <span key={variable} className={styles.topicHelpPanel__chip}>
                                            {variable}
                                        </span>
                                    ))}
                                </div>
                            </div>
                            <div className={styles.topicHelpPanel__example}>
                                <span className={styles.topicHelpPanel__variablesLabel}>
                                    {txt('MqttPublisher.TopicHelpExample')}
                                </span>
                                <span>969161b0-4712.../7a4bce30-313b.../2969000000</span>
                            </div>
                        </div>
                    )}

                    <div className={styles.eventTypesLabel}>{txt('MqttPublisher.EventTypes')}</div>
                    {MQTT_EVENT_TYPES.map(({ value, label, info }) => (
                        <CheckBox
                            key={value}
                            id={`event-type-${value}`}
                            label={
                                <span className={styles.eventTypeLabel}>
                                    {txt(label)}
                                    <InputInfo infoText={info} />
                                </span>
                            }
                            labelIsElement
                            checked={eventTypes.includes(value)}
                            onChange={(): void =>
                                setEventTypes(prev =>
                                    prev.includes(value) ? prev.filter(e => e !== value) : [...prev, value]
                                )
                            }
                        />
                    ))}
                </CollapsibleSection>
            </div>

            <div className="page-wrapper__medium">
                <CollapsibleSection
                    title={txt('MqttPublisher.Units')}
                    defaultExpanded={false}
                    collapsedContent={
                        <span>
                            {[
                                sensorUnitLabels[sensorUnits.radonUnit],
                                sensorUnits.tempUnit === 'c' ? txt('Units.UnitCelsius') : txt('Units.UnitFahrenheit'),
                                sensorUnitLabels[sensorUnits.pressureUnit],
                                sensorUnitLabels[sensorUnits.vocUnit],
                            ].join(' · ')}
                            {'  ·  '}
                            {txt('MqttPublisher.UnitsPreFilled')}
                        </span>
                    }
                    testAttr="section-units"
                >
                    <div className={styles.unitsHintRow}>
                        <span className={styles.unitsHint}>{txt('MqttPublisher.UnitsPreFilled')}</span>
                    </div>
                    {(
                        [
                            {
                                label: 'Radon',
                                key: 'radonUnit' as const,
                                options: [
                                    { value: 'bq', label: sensorUnitLabels.bq },
                                    { value: 'pci', label: sensorUnitLabels.pci },
                                ],
                            },
                            {
                                label: 'Temperature',
                                key: 'tempUnit' as const,
                                options: [
                                    { value: 'c', label: txt('Units.UnitCelsius') },
                                    { value: 'f', label: txt('Units.UnitFahrenheit') },
                                ],
                            },
                            {
                                label: 'Pressure',
                                key: 'pressureUnit' as const,
                                options: [
                                    { value: 'hpa', label: sensorUnitLabels.hpa },
                                    { value: 'inhg', label: sensorUnitLabels.inhg },
                                ],
                            },
                            {
                                label: 'VOC',
                                key: 'vocUnit' as const,
                                options: [
                                    { value: 'ppb', label: sensorUnitLabels.ppb },
                                    { value: 'mgpc', label: sensorUnitLabels.mgpc },
                                ],
                            },
                        ] as const
                    ).map(({ label, key, options }) => (
                        <div key={key} className={styles.unitRow}>
                            <span className={styles.unitRow__label}>{txt(label)}</span>
                            <div className={styles.unitToggle}>
                                {options.map(option => (
                                    <button
                                        key={option.value}
                                        type="button"
                                        className={`${styles.unitToggle__option} ${
                                            sensorUnits[key] === option.value
                                                ? styles['unitToggle__option--active']
                                                : ''
                                        }`}
                                        onClick={(): void => setSensorUnits({ ...sensorUnits, [key]: option.value })}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                </CollapsibleSection>
            </div>

            <div className="page-wrapper__medium">
                <div className={styles.createButtonRow}>
                    <PrimaryButton
                        onClick={handleSubmit}
                        type="button"
                        title={isEditMode ? 'MqttPublisher.Update' : 'MqttPublisher.Create'}
                        loading={submitLoading}
                        color="primary"
                        testAttr={isEditMode ? 'update-mqtt-publisher' : 'create-mqtt-publisher'}
                    />
                </div>
            </div>

            <SelectionModal
                isOpen={showBuildingsModal}
                onClose={(): void => setShowBuildingsModal(false)}
                items={buildingItems}
                selectedIds={effectiveSelectedIds}
                onSave={handleBuildingsSave}
                searchPosition="top"
                showColumnActions
                compactButtons
                title={txt('MqttPublisher.SelectBuildings')}
                availableTitle={txt('MqttPublisher.AllBuildings')}
                selectedTitle={txt('MqttPublisher.SelectedBuildings', { count: 0 }).replace(/\s*\(\d+\)/, '')}
                emptyMessage={txt('MqttPublisher.NoBuildings')}
                allSelectedMessage={txt('MqttPublisher.AllBuildingsSelected')}
                noSelectionMessage={txt('MqttPublisher.NoBuildingsSelected')}
            />
        </form>
    );
};

export default MqttPublisherCreateForm;
